import type { KeyboardEvent } from 'react';

/**
 * Roving keyboard navigation for an ARIA tablist.
 *
 * Attach to the `onKeyDown` of the element carrying `role="tablist"`.
 * Moves focus (and activates via a synthetic click, reusing each tab's
 * existing `onClick`) between elements with `role="tab"` using the arrow
 * keys, plus Home/End. Purely behavioural — no visual changes.
 */
export function handleTablistArrowKeys(e: KeyboardEvent<HTMLElement>): void {
  const navKeys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
  if (!navKeys.includes(e.key)) return;

  const container = e.currentTarget;
  const tabs = Array.from(
    container.querySelectorAll<HTMLElement>('[role="tab"]'),
  ).filter((t) => !t.hasAttribute('disabled') && (t as HTMLButtonElement).disabled !== true);
  if (tabs.length === 0) return;

  const currentIndex = tabs.findIndex((t) => t === document.activeElement);
  let nextIndex = currentIndex;

  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % tabs.length;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      nextIndex = currentIndex < 0 ? tabs.length - 1 : (currentIndex - 1 + tabs.length) % tabs.length;
      break;
    case 'Home':
      nextIndex = 0;
      break;
    case 'End':
      nextIndex = tabs.length - 1;
      break;
  }

  if (nextIndex >= 0 && nextIndex !== currentIndex) {
    e.preventDefault();
    tabs[nextIndex].focus();
    tabs[nextIndex].click();
  }
}
